import React, { useCallback, useEffect, useState } from "react";
import { Activity, ChevronDown, ChevronUp, RefreshCw, HardHat, Building2, Bot, Image as ImageIcon } from "lucide-react";
import { fmtDT } from "../../lib/date";

const SIDE = {
  sub: { zh: "分包商", en: "Subcontractor", dot: "bg-amber-500", chip: "bg-amber-100 text-amber-800 border-amber-200", Icon: HardHat },
  company: { zh: "公司", en: "Company", dot: "bg-sky-500", chip: "bg-sky-50 text-sky-800 border-sky-200", Icon: Building2 },
  system: { zh: "系统", en: "System", dot: "bg-slate-400", chip: "bg-slate-100 text-slate-600 border-slate-200", Icon: Bot },
};
const ago = (iso, zh) => {
  const d = new Date(iso); if (isNaN(d)) return "";
  const m = Math.round((Date.now() - d.getTime()) / 60000);
  if (m < 60) return zh ? `${Math.max(m, 0)} 分钟前` : `${Math.max(m, 0)}m ago`;
  const h = Math.round(m / 60); if (h < 48) return zh ? `${h} 小时前` : `${h}h ago`;
  const days = Math.round(h / 24); return zh ? `${days} 天前` : `${days}d ago`;
};

/* 分包卡顶部「分包商最近动作」时间线：分包商动作（琥珀）与公司侧关键动作（蓝）混排，默认 8 条，可展开全部。与分包商管理 / 任务链同一数据。 */
export function SubTimeline({ api, sc, zh, reloadKey = 0 }) {
  const [data, setData] = useState(null);
  const [all, setAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const load = useCallback(async (full) => {
    setLoading(true); setError(false);
    try { const { data } = await api.get(`/subcontracts/${sc.id}/timeline`, { params: { limit: full ? 0 : 8 } }); setData(data); }
    // 网络失败不能伪装成没有记录；保留旧内容并显示可重试状态。
    catch { setError(true); }
    setLoading(false);
  }, [api, sc.id]);
  useEffect(() => { load(all); }, [load, all, reloadKey]);
  const items = data?.items || [];
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid={`sub-timeline-${sc.id}`} data-count={items.length} data-total={data?.total ?? -1}>
      {error && <p role="alert" className="text-sm text-destructive">{zh ? '记录加载失败，请点击刷新重试。' : 'Activity failed to load. Use refresh to retry.'}</p>}
      <div className="flex items-center gap-2 flex-wrap">
        <Activity className="h-3.5 w-3.5 text-amber-700" />
        <span className="text-xs font-semibold text-amber-900">{zh ? "分包商最近动作" : "Recent subcontractor activity"}</span>
        {data && <span className="text-xs text-slate-500" data-testid={`sub-timeline-summary-${sc.id}`}>{zh ? `分包商 ${data.subCount} 条 · 共 ${data.total} 条` : `${data.subCount} by sub · ${data.total} total`}{data.lastSubAt ? ` · ${zh ? "最近反馈" : "last"} ${ago(data.lastSubAt, zh)}` : ""}</span>}
        <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-slate-500">
          {Object.entries(SIDE).map(([k, s]) => <span key={k} className="inline-flex items-center gap-0.5"><span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />{zh ? s.zh : s.en}</span>)}
        </span>
        <button type="button" onClick={() => load(all)} className="text-slate-400 hover:text-slate-700" title={zh ? "刷新" : "Refresh"} data-testid={`sub-timeline-refresh-${sc.id}`}><RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /></button>
      </div>
      {data && items.length === 0 && <p className="text-xs text-slate-400 mt-1" data-testid={`sub-timeline-empty-${sc.id}`}>{zh ? "暂无记录" : "No activity yet."}</p>}
      {items.length > 0 && (
        <ol className="mt-1.5 space-y-1" data-testid={`sub-timeline-list-${sc.id}`}>
          {items.map((e, i) => {
            const s = SIDE[e.side] || SIDE.company;
            return (
              <li key={`${e.at}-${i}`} className="flex flex-wrap sm:flex-nowrap items-start gap-x-2 gap-y-0.5 text-xs" data-testid={`sub-timeline-item-${sc.id}-${i}`} data-side={e.side} data-kind={e.kind}>
                <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${s.dot}`} />
                <span className="text-slate-400 whitespace-nowrap tabular-nums w-[110px] shrink-0" title={e.at}>{fmtDT(e.at)}</span>
                <span className={`inline-flex items-center gap-0.5 text-xs px-1.5 py-px rounded-full border shrink-0 max-w-[160px] truncate ${s.chip}`}><s.Icon className="h-2.5 w-2.5 shrink-0" />{e.by || (zh ? s.zh : s.en)}</span>
                <span className="min-w-0 basis-full pl-[14px] sm:basis-auto sm:flex-1 sm:pl-0 break-words">
                  <span className={`font-medium ${e.side === "sub" ? "text-amber-900" : "text-slate-800"}`}>{zh ? e.title : e.titleEn || e.title}</span>
                  {e.detail && <span className="text-slate-500"> · {e.detail}</span>}
                  {e.photos > 0 && <span className="inline-flex items-center gap-0.5 ml-1 text-slate-400"><ImageIcon className="h-2.5 w-2.5" />{e.photos}</span>}
                </span>
              </li>
            );
          })}
        </ol>
      )}
      {data && data.total > 8 && (
        <button type="button" onClick={() => setAll((v) => !v)} data-testid={`sub-timeline-toggle-${sc.id}`} className="mt-1.5 inline-flex items-center gap-0.5 text-xs font-semibold text-amber-800 hover:underline">
          {all ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}{all ? (zh ? "只看最近 8 条" : "Show latest 8") : (zh ? `展开全部 ${data.total} 条` : `Show all ${data.total}`)}
        </button>
      )}
    </div>
  );
}
